import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { isMatrixSingleOrMultipleNode, isNewMatrixNode } from 'utils/nodeTypes';
import findQuestionByRefId from 'utils/findQuestionByRefId';
import { EDIT_PERMISSIONS } from 'containers/Permissions';
import useHasPermission from 'containers/Permissions/hooks/useHasPermission';
import { isAnswerWithLogic } from 'containers/QuestionnairePage/GenericQuestion/utils';
import { hasAnswerDisplayLogic } from 'components/VisibilityActionButton/utils';
import {
  makeSelectFormattedNodes,
  makeSelectAnswersWithLogic,
  makeSelectAnswersWithLogicFromOutcomesHide,
} from 'containers/QuestionnairePage/selectors';
import { makeSelectQuestionQuotasMinimal } from 'containers/QuestionnairePage/selectorUtils/quotasMinimal';
import { selectQuestionnaireLogicQl } from 'containers/QuestionnairePage/selectorUtils/screenerTypes';
import { wouldChangeRemoveQuotaEligibility } from 'containers/QuestionnairePage/utils';
import {
  addUnconditionalHideAnswer,
  removeUnconditionalHideAnswer,
} from 'containers/QuestionnaireLogic/actions';
import { QUOTA_MODE } from 'data/quotaModes';

function useStatePropsAndActions({ nodeRefId, valueRefId, valueType }) {
  const actionPayload = useMemo(
    () => ({ nodeRefId, valueRefId, valueType }),
    [nodeRefId, valueRefId, valueType],
  );

  const answersWithLogic = useSelector(makeSelectAnswersWithLogic);

  const answersWithLogicFromOutcomesHide = useSelector(
    makeSelectAnswersWithLogicFromOutcomesHide,
  );

  const canUnconditionalHideAnswer = useHasPermission(
    EDIT_PERMISSIONS.UNCONDITIONAL_HIDE_ANSWER,
  );
  const canUnconditionalHideAnswerReset = useHasPermission(
    EDIT_PERMISSIONS.UNCONDITIONAL_HIDE_ANSWER_RESTORE,
  );

  const questionQuotas = useSelector(makeSelectQuestionQuotasMinimal);
  const ql = useSelector(selectQuestionnaireLogicQl);

  const nodes = useSelector(makeSelectFormattedNodes);
  const node = findQuestionByRefId(nodes, nodeRefId);

  const quotaModeToBeCleared = useMemo(() => {
    const quotaMode = questionQuotas[nodeRefId]?.quotaMode || QUOTA_MODE.NONE;
    if (quotaMode === QUOTA_MODE.NONE) return QUOTA_MODE.NONE;

    const parentNode =
      nodes.find(({ questions = [] }) =>
        questions.some(({ refId }) => refId === nodeRefId),
      ) || null;

    const crossesThreshold = wouldChangeRemoveQuotaEligibility({
      node,
      parentNode,
      before: {
        ql,
        unconditionallyHiddenItems: answersWithLogicFromOutcomesHide,
      },
      after: {
        ql,
        unconditionallyHiddenItems: [
          ...answersWithLogicFromOutcomesHide,
          valueRefId,
        ],
      },
    });

    return crossesThreshold ? quotaMode : QUOTA_MODE.NONE;
  }, [
    questionQuotas,
    nodes,
    node,
    nodeRefId,
    valueRefId,
    answersWithLogicFromOutcomesHide,
    ql,
  ]);

  const nodeIsMatrix = isMatrixSingleOrMultipleNode(node);
  const nodeIsNewMatrix = isNewMatrixNode(node);

  const optionObject = { refId: valueRefId };
  const hasLogic = isAnswerWithLogic({
    answer: optionObject,
    answersWithLogic,
  });
  const hasHideLogic = hasAnswerDisplayLogic(
    optionObject,
    answersWithLogicFromOutcomesHide,
  );

  const dispatch = useDispatch();

  return {
    hasLogic,
    hasHideLogic,
    actionPayload,
    quotaModeToBeCleared,
    insideMatrix: nodeIsMatrix,
    insideNewMatrix: nodeIsNewMatrix,
    canUnconditionalHideAnswer,
    canUnconditionalHideAnswerReset,
    onAddUnconditionalHideAnswer: useCallback(
      (payload) => {
        dispatch(addUnconditionalHideAnswer(payload));
      },
      [dispatch],
    ),
    onRemoveUnconditionalHideAnswer: useCallback(
      (payload) => {
        dispatch(removeUnconditionalHideAnswer(payload));
      },
      [dispatch],
    ),
  };
}

export default useStatePropsAndActions;
